/**
 * WidgetPreview Component
 * Modal that displays live widget preview
 */

import * as React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';

export interface WidgetPreviewProps {
  isOpen: boolean;
  onClose: () => void;
  widgetUrl?: string;
  slug: string;
}

const WIDGET_BASE_URL = import.meta.env.VITE_WIDGET_URL || 'http://localhost:8787/widget';

/**
 * WidgetPreview renders the booking widget in a modal iframe
 * Used to preview configuration changes before publishing
 */
export function WidgetPreview({
  isOpen,
  onClose,
  slug,
}: WidgetPreviewProps) {
  const previewUrl = `${WIDGET_BASE_URL}/${slug}?preview=true`;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl h-[90vh]">
        <DialogHeader>
          <DialogTitle>Widget Preview</DialogTitle>
          <p className="text-sm text-gray-600">
            Preview how your widget will appear to customers
          </p>
        </DialogHeader>

        <div className="flex-1 overflow-hidden rounded-lg border border-gray-200">
          <iframe
            src={previewUrl}
            title="Widget Preview"
            className="w-full h-full"
            style={{ minHeight: '600px' }}
          />
        </div>

        <div className="flex justify-between items-center pt-4 border-t">
          <a
            href={previewUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-primary-600 hover:text-primary-700 underline"
          >
            Open in new tab
          </a>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-900 rounded-md hover:bg-gray-300 transition-colors"
          >
            Close
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export interface PreviewButtonProps {
  slug: string;
  className?: string;
}

/**
 * PreviewButton opens widget preview in modal
 */
export function PreviewButton({ slug, className }: PreviewButtonProps) {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className={className}
        type="button"
      >
        Preview Widget
      </button>
      <WidgetPreview
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        slug={slug}
      />
    </>
  );
}
